import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { BudgetVersionType } from '@callmart/types';
import { useBudgetVersions } from '../../hooks/api/budgetVersions';
import { useBudgetBuckets } from '../../hooks/api/budgetBuckets';
import { BudgetVersion, exportBudgetToExcel } from '../../services/budgetVersion';
import { BudgetBucket } from '../../services/budgetBucket';
import { useDispatchErrorNotification } from '../../hooks';
import { currentAppYear } from '../../utils/dateUtils';
import UserContext from '../User/UserContext';
import dayjs from '@utils/dayjs';

const STORAGE_KEY = 'budgetVersionId';
const BUCKET_STORAGE_KEY = 'budgetBucketId';

// Default landing version on a clean entry: the version at the top of the list. Budget versions
// come back newest-first (the API orders them by id DESC), so the first entry is the most recently
// created version — what users expect to see selected when they open the section.
export const getDefaultVersion = (versions: BudgetVersion[]): BudgetVersion | undefined => versions[0];

// CALL-800: the versions a bucket filter admits — everything under "All buckets" (undefined). One
// spelling, because the selection is resolved against it AND written against it on a bucket change,
// and the two answering differently is the whole bug this scoping fixes.
const versionsInBucket = (versions: BudgetVersion[], bucketId: number | undefined): BudgetVersion[] =>
    bucketId == null ? versions : versions.filter((v) => v.budgetBucketId === bucketId);

interface BudgetContextValue {
    selectedVersionId: number | undefined;
    // CALL-800: `bucketIdHint` names the version's bucket for a version the `versions` list does not
    // hold yet (a freshly created draft, selected before the query refetches) — the selection moves
    // the bucket filter with it, and it can only do that if it knows where the version lives.
    setSelectedVersionId: (id: number | undefined, bucketIdHint?: number) => void;
    versions: BudgetVersion[];
    isVersionsLoading: boolean;
    selectedVersion: BudgetVersion | undefined;
    // CALL-758: budget buckets (silos). `selectedBucketId` is the bucket filter the Billing
    // Configuration and Prices & ABT pages manage — it defaults to "All buckets" (undefined) and can
    // be switched to a specific bucket. CALL-800: the selected version is resolved INSIDE it, so
    // whichever page changes the bucket, the version (and every figure derived from it) follows.
    buckets: BudgetBucket[];
    isBucketsLoading: boolean;
    // `selectedBucketId` is the bucket filter shared by the overview version list and the config /
    // prices pages: `undefined` = "All buckets" (the default — shows everything).
    selectedBucketId: number | undefined;
    setSelectedBucketId: (id: number | undefined) => void;
    handleDownload: () => void;
    isDownloading: boolean;
    isSnapshot: boolean;
    canEdit: boolean;
    isReadOnly: boolean;
    // CALL-752/756: the "Mit Extrapolation" toggle lives here so the Ist Budget grid AND the export
    // (handleDownload) share one state — the download reflects exactly what is on screen.
    showExtrapolation: boolean;
    setShowExtrapolation: (value: boolean) => void;
}

// CALL-745 follow-up: the scrolling month window (first visible month) lives in its OWN context so
// that moving it re-renders only the month grids — not the version selector or other BudgetContext
// consumers, which would otherwise re-render on every arrow click (shared value identity).
interface BudgetWindowContextValue {
    windowStart: string;
    setWindowStart: (value: string) => void;
    // Relative move (±months) via a functional state update, so rapid clicks always advance from
    // the latest committed window — clicks are never dropped while the grids re-render.
    shiftWindow: (deltaMonths: number) => void;
    // CALL-815: the Overview's optional `Datum von` / `Datum bis` filter, as month keys ('' = not set).
    // It lives HERE, beside windowStart, for the same reason windowStart does: its consumers are the month
    // grids, so changing it must not re-render the version selector and the rest of the budget UI.
    //
    // That split is about re-renders and enforces NOTHING about the export — handleDownload is declared in
    // the same provider body and has `range` in scope. What keeps the AC ("the export stays the whole budget
    // period") true is that no export or import request takes a month at all — that is every one of them:
    // exportBudgetToExcel(id, extrapolate), exportOnTopCosts(versionId), downloadOnTopCostsImportTemplate(),
    // importOnTopCosts(versionId, file), importBillingConfigs(versionId, file),
    // downloadBillingConfigImportTemplate(variant) and exportBudgetScenarios({ versionId, scenarioIds }).
    // The server derives the workbook's months from the version. budgetContextBucketScope's export test pins
    // the call signature so a month parameter cannot be added to it unnoticed.
    //
    // Held raw, including a period that does not (yet) make sense: isBudgetRangeActive decides whether
    // it is applied, so the half-typed state of picking two dates needs no second copy to sync.
    rangeFrom: string;
    rangeTo: string;
    setRange: (from: string, to: string) => void;
}

const BudgetContext = createContext<BudgetContextValue | null>(null);
const BudgetWindowContext = createContext<BudgetWindowContextValue | null>(null);

export const BudgetContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const versionIdParam = searchParams.get('versionId');

    const [versions, isVersionsLoading] = useBudgetVersions();
    const [buckets, isBucketsLoading] = useBudgetBuckets();
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    const bucketIdParam = searchParams.get('bucketId');

    // CALL-758: the selected bucket, shared by the overview version filter and the config / prices
    // pages. `undefined` means "All buckets" — the default — so the overview lists every version
    // and the config/prices grids show every bucket's rows. Precedence:
    //   1. URL ?bucketId= — explicit deep-link (only if it points at a real bucket)
    //   2. sessionStorage — the user's last explicit pick
    //   3. undefined — All buckets
    //
    // Resolved BEFORE the version, which is scoped to it (CALL-800).
    const selectedBucketId = useMemo(() => {
        if (buckets.length === 0) return undefined;

        if (bucketIdParam) {
            const fromUrl = parseInt(bucketIdParam, 10);
            if (buckets.some((b) => b.id === fromUrl)) return fromUrl;
        }

        const stored = sessionStorage.getItem(BUCKET_STORAGE_KEY);
        if (stored) {
            const storedNum = parseInt(stored, 10);
            if (buckets.some((b) => b.id === storedNum)) {
                // CALL-800: a deep-linked ?versionId= carries its own bucket, and that outranks the one
                // THIS TAB happens to remember. The version is resolved inside the selected bucket, so
                // a remembered bucket would otherwise substitute one of its own versions and show the
                // recipient of a shared link different figures than the sender. Only against a
                // REMEMBERED bucket: "All buckets" stores nothing, so browsing across buckets is never
                // collapsed onto the bucket of the version picked there.
                const linkedBucketId = versionIdParam
                    ? versions.find((v) => v.id === parseInt(versionIdParam, 10))?.budgetBucketId
                    : undefined;
                return linkedBucketId != null && buckets.some((b) => b.id === linkedBucketId)
                    ? linkedBucketId
                    : storedNum;
            }
        }

        return undefined;
    }, [buckets, bucketIdParam, versions, versionIdParam]);

    // The effective selection is COMPUTED, never auto-written, so a plain visit to /budget leaves
    // the address bar untouched. Order of precedence:
    //   1. URL ?versionId= — explicit deep-link / shareable (used only if it points at a real version)
    //   2. sessionStorage — the user's last explicit pick, kept across sidebar nav that drops the param
    //   3. Default — the most recent version, i.e. the top of the newest-first list (getDefaultVersion)
    //
    // CALL-800: every tier resolves inside the SELECTED BUCKET. The two are one selection — the version
    // decides what the pages show, the bucket names what they are showing — but the pages that change
    // the bucket (Prices & ABT, the bucket manager) write only the bucket, so the version from the
    // bucket left behind stayed selected and the overview drew that bucket's figures under the new
    // bucket's name. Scoping the resolution fixes every one of those callers at once, plus a
    // sessionStorage entry or a hand-made link with the two out of step.
    const selectedVersionId = useMemo(() => {
        const inBucket = versionsInBucket(versions, selectedBucketId);
        // No versions at all, or a bucket that holds none yet (a just-created one, until its
        // auto-created draft arrives): nothing is selected, and the pages show their "pick a
        // version" hint rather than another bucket's numbers.
        if (inBucket.length === 0) return undefined;

        if (versionIdParam) {
            const fromUrl = parseInt(versionIdParam, 10);
            if (inBucket.some((v) => v.id === fromUrl)) return fromUrl;
        }

        const storedId = sessionStorage.getItem(STORAGE_KEY);
        if (storedId) {
            const storedNum = parseInt(storedId, 10);
            if (inBucket.some((v) => v.id === storedNum)) return storedNum;
        }

        return getDefaultVersion(inBucket)?.id;
    }, [versions, versionIdParam, selectedBucketId]);

    const selectedVersion = useMemo(
        () => versions.find((v) => v.id === selectedVersionId),
        [versions, selectedVersionId]
    );

    // An explicit bucket pick writes ?bucketId= (+ sessionStorage); the auto-computed default never
    // touches the address bar.
    const setSelectedBucketId = useCallback(
        (id: number | undefined) => {
            // CALL-800: the version the new bucket leaves selected is RECORDED here, not left to be
            // recomputed on every render. Both are one selection, and a bucket change is as explicit a
            // choice as picking the version itself, so it may write one: the resolution above would
            // otherwise keep reading the version of the bucket just left — invisible under the new
            // bucket, but valid again the moment the filter widens to "All buckets", which then jumped
            // back to it. A version the new bucket holds is left exactly as it is (whatever tier it came
            // from), so narrowing to the bucket you are already looking at changes nothing.
            const keepsVersion =
                selectedVersionId != null && versionsInBucket(versions, id).some((v) => v.id === selectedVersionId);
            const nextVersionId =
                selectedVersionId == null || keepsVersion
                    ? undefined
                    : getDefaultVersion(versionsInBucket(versions, id))?.id;

            // One update for both params: two setSearchParams calls in a tick each start from the same
            // params, so the second would silently drop the first's write.
            setSearchParams(
                (prev) => {
                    if (id !== undefined) {
                        prev.set('bucketId', String(id));
                        sessionStorage.setItem(BUCKET_STORAGE_KEY, String(id));
                    } else {
                        prev.delete('bucketId');
                        sessionStorage.removeItem(BUCKET_STORAGE_KEY);
                    }
                    if (!keepsVersion && selectedVersionId != null) {
                        if (nextVersionId !== undefined) {
                            prev.set('versionId', String(nextVersionId));
                            sessionStorage.setItem(STORAGE_KEY, String(nextVersionId));
                        } else {
                            // The bucket holds no versions yet — nothing to point at.
                            prev.delete('versionId');
                            sessionStorage.removeItem(STORAGE_KEY);
                        }
                    }
                    return prev;
                },
                { replace: true }
            );
        },
        [setSearchParams, versions, selectedVersionId]
    );

    // Writing the selection to the URL (+ sessionStorage) is reserved for an EXPLICIT user choice
    // (dropdown change, bucket change, or clearing after a delete). An auto-computed default must
    // never touch the address bar.
    const setSelectedVersionId = useCallback(
        (id: number | undefined, bucketIdHint?: number) => {
            // CALL-800: a version picked from OUTSIDE the current bucket takes the bucket filter with
            // it — the consolidated view's bucket+version picker and a draft created in another bucket
            // both hand one over. Without this the bucket-scoped resolution above would drop the pick
            // on the next render and snap back to the filtered bucket's newest version. "All buckets"
            // is left alone: nothing there needs narrowing. The hint is for a version the list does not
            // know yet (a just-created draft is selected before its query refetches).
            const nextBucketId =
                id === undefined ? undefined : (bucketIdHint ?? versions.find((v) => v.id === id)?.budgetBucketId);
            const followBucket = selectedBucketId != null && nextBucketId != null && nextBucketId !== selectedBucketId;

            // Both params in ONE update: two setSearchParams calls in a tick each start from the same
            // params, so the second silently drops the first's write.
            setSearchParams(
                (prev) => {
                    if (id !== undefined) {
                        prev.set('versionId', String(id));
                        sessionStorage.setItem(STORAGE_KEY, String(id));
                    } else {
                        prev.delete('versionId');
                        sessionStorage.removeItem(STORAGE_KEY);
                    }
                    if (followBucket) {
                        prev.set('bucketId', String(nextBucketId));
                        sessionStorage.setItem(BUCKET_STORAGE_KEY, String(nextBucketId));
                    }
                    return prev;
                },
                { replace: true }
            );
        },
        [setSearchParams, versions, selectedBucketId]
    );

    // Shared scrolling month window (first visible month). Defaults to January of the current
    // year; grids derive their 12 columns from it via getWindowMonths.
    //
    // CALL-781: the year comes from the APP timezone, not browser-local time. The "hide past months"
    // cutoff is the app-timezone current month, and the two have to agree: a browser still in the old
    // year (New York at 2026-12-31 18:30, when Berlin is already 2027) opened the Jan–Dec 2026 window
    // against a Jan 2027 cutoff, and with the filter on that hid every month column.
    const [windowStart, setWindowStartState] = useState<string>(`${currentAppYear()}-01-01`);

    const setWindowStart = useCallback((value: string) => {
        setWindowStartState(dayjs(value).startOf('month').format('YYYY-MM-DD'));
    }, []);

    const shiftWindow = useCallback((deltaMonths: number) => {
        setWindowStartState((prev) => dayjs(prev).add(deltaMonths, 'month').startOf('month').format('YYYY-MM-DD'));
    }, []);

    // CALL-815: the flexible date range, as ONE state so the two ends always change together (clearing
    // either one clears the period). Session state on purpose — deliberately NOT persisted, unlike the
    // hide-past checkbox: a remembered range would silently hide months on the next visit.
    const [range, setRangeState] = useState({ from: '', to: '' });
    const setRange = useCallback((from: string, to: string) => setRangeState({ from, to }), []);

    // CALL-745: budget versions no longer carry a year range, so on version change we reset the
    // window to the current year (a predictable landing spot). The window is a pure view lens —
    // the user can scroll to any month; grids render whatever data exists there.
    const prevVersionRef = useRef(selectedVersionId);
    useEffect(() => {
        if (prevVersionRef.current === selectedVersionId) return;
        prevVersionRef.current = selectedVersionId;
        if (selectedVersionId) setWindowStart(`${currentAppYear()}-01-01`);
    }, [selectedVersionId, setWindowStart]);

    const isSnapshot = selectedVersion?.type === BudgetVersionType.SNAPSHOT;

    const user = useContext(UserContext);
    const canEdit = Boolean(user?.isBudgetUser);
    const isReadOnly = !canEdit || isSnapshot;

    const [isDownloading, setIsDownloading] = useState(false);
    // CALL-752: the Ist Budget extrapolation toggle (default on). Shared with the export so the
    // downloaded file matches the on-screen view.
    const [showExtrapolation, setShowExtrapolation] = useState(true);
    // CALL-752: a snapshot is the immutable budget of record — it freezes ENTERED rates only and is
    // never extrapolated (extrapolation is a draft-time projection/preview aid + an export option).
    // So snapshots always render entered-only (no blue, no live projection); editable versions follow
    // the "Mit Extrapolation" toggle. The toggle is hidden for snapshots in BudgetOverview.
    const effectiveShowExtrapolation = isSnapshot ? false : showExtrapolation;

    const handleDownload = useCallback(async () => {
        if (!selectedVersionId) return;
        setIsDownloading(true);
        try {
            // CALL-752: export the EFFECTIVE mode — a snapshot is entered-only, so a left-on toggle
            // can't make the export request projections it must not contain (the backend also enforces
            // this, but the request shouldn't contradict it). For a draft this equals the toggle.
            const blob = await exportBudgetToExcel(selectedVersionId, effectiveShowExtrapolation);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const rawName = versions.find((v) => v.id === selectedVersionId)?.name ?? String(selectedVersionId);
            const safeName = rawName.replace(/[^a-zA-Z0-9\-_. ]/g, '_');
            // CALL-756: mark the extrapolation mode in the filename so an extrapolated export and an
            // entered-only one can't be confused. A snapshot is entered-only (frozen) → no marker.
            const suffix = isSnapshot ? '' : effectiveShowExtrapolation ? '-mit-Extrapolation' : '-ohne-Extrapolation';
            link.download = `budget-${safeName}${suffix}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to export budget.';
            dispatchErrorNotification({ message });
        } finally {
            setIsDownloading(false);
        }
    }, [selectedVersionId, versions, dispatchErrorNotification, effectiveShowExtrapolation, isSnapshot]);

    const value = useMemo<BudgetContextValue>(
        () => ({
            selectedVersionId,
            setSelectedVersionId,
            versions,
            isVersionsLoading,
            selectedVersion,
            buckets,
            isBucketsLoading,
            selectedBucketId,
            setSelectedBucketId,
            handleDownload,
            isDownloading,
            isSnapshot,
            canEdit,
            isReadOnly,
            showExtrapolation: effectiveShowExtrapolation,
            setShowExtrapolation,
        }),
        [
            selectedVersionId,
            setSelectedVersionId,
            versions,
            isVersionsLoading,
            selectedVersion,
            buckets,
            isBucketsLoading,
            selectedBucketId,
            setSelectedBucketId,
            handleDownload,
            isDownloading,
            isSnapshot,
            canEdit,
            isReadOnly,
            effectiveShowExtrapolation,
        ]
    );

    const windowValue = useMemo<BudgetWindowContextValue>(
        () => ({ windowStart, setWindowStart, shiftWindow, rangeFrom: range.from, rangeTo: range.to, setRange }),
        [windowStart, setWindowStart, shiftWindow, range, setRange]
    );

    return (
        <BudgetContext.Provider value={value}>
            <BudgetWindowContext.Provider value={windowValue}>{children}</BudgetWindowContext.Provider>
        </BudgetContext.Provider>
    );
};

export const useBudgetContext = () => {
    const context = useContext(BudgetContext);

    if (!context) {
        throw new Error('useBudgetContext must be used within BudgetContextProvider');
    }
    return context;
};

// Subscribe to the scrolling month window only. Components that read the window (month grids,
// MonthWindowNav) use this so a window change doesn't re-render the rest of the budget UI.
export const useBudgetWindow = () => {
    const context = useContext(BudgetWindowContext);

    if (!context) {
        throw new Error('useBudgetWindow must be used within BudgetContextProvider');
    }
    return context;
};
